import FW from '../fw/fw';
import LocalStorage from '../fw/local-storage';


export default class VolumeManagement {

  #rmp;
  #volumeMouseupFn = null;
  #volumeMousemoveFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #updateVolumeBarUI(currentVolume) {
    const currentVolumePerCentString = (currentVolume * 100) + '%';
    this.#rmp.dom.volumeBarSlidingCurrentPosition.style.width = currentVolumePerCentString;
  }

  #onVolumeChange() {
    if (this.#rmp.audioOnly) {
      return;
    }
    const keyboardShortcutLabel = this.#rmp.labels.hint.keyboardShortcut;
    const isMuted = this.#rmp.mute;
    let currentVolume = 0;
    this.#rmp.dom.volume.classList.remove('rmp-i-volume-off', 'rmp-i-volume-half', 'rmp-i-volume');
    if (!isMuted) {
      this.#rmp.dom.volume.setAttribute(
        'aria-label',
        this.#rmp.labels.hint.mute + ' ' + keyboardShortcutLabel + ' m'
      );
      currentVolume = this.#rmp.volume;
      this.updateVolumeIcon(currentVolume);
    } else {
      this.#rmp.dom.volume.setAttribute(
        'aria-label',
        this.#rmp.labels.hint.unmute + ' ' + keyboardShortcutLabel + ' m'
      );
      this.#rmp.dom.volume.classList.add('rmp-i-volume-off');
    }
    if (currentVolume >= 0 && currentVolume <= 1) {
      if (this.#rmp.rememberVolume && LocalStorage.available(this.#rmp.allowLocalStorage)) {
        LocalStorage.writeVolume(currentVolume);
      }
      this.#updateVolumeBarUI(currentVolume);
    }
    // update aria-valuenow
    this.#rmp.dom.volumeBar.setAttribute('aria-valuenow', FW.precisionRound(currentVolume * 100, 2));
    this.#rmp.dom.volumeBar.setAttribute('aria-valuetext',
      (FW.precisionRound(currentVolume * 100, 2)).toString() + '%');
    // fire event
    this.#rmp.dispatch('volumechange');
  }

  #getNewVolumeBasedOnPosX(event) {
    const posX = FW.getPosX(event);
    const volumeBarContainerWidth = FW.getPrecisionWidthWithPaddingAndBorder(this.#rmp.dom.volumeBarSlidingArea);
    const volumeBarContainerLeft = FW.getAbsLeft(this.#rmp.dom.volumeBarSlidingArea);
    let newVolume;
    if (posX >= (volumeBarContainerWidth + volumeBarContainerLeft)) {
      newVolume = 1;
    } else if (posX <= volumeBarContainerLeft) {
      newVolume = 0;
    } else {
      newVolume = Math.abs(posX - volumeBarContainerLeft) / volumeBarContainerWidth;
    }
    return newVolume;
  }

  #volumeMouseup(event) {
    if (event) {
      event.stopPropagation();
      this.#rmp.dom.volumeBarSlidingCurrentPosition.classList.remove('grabbing');
      this.destroy();
      const newVolume = this.#getNewVolumeBasedOnPosX(event);
      this.#updateVolumeBarUI(newVolume);
      this.#rmp.volume = newVolume;
    }
  }

  #volumeMousemove(event) {
    const newVolume = this.#getNewVolumeBasedOnPosX(event);
    this.#updateVolumeBarUI(newVolume);
    this.#rmp.volume = newVolume;
  }

  #onClickVolumeIcon(event) {
    FW.eventStopPrevent(event);
    if (this.#rmp.mute) {
      this.#rmp.mute = false;
    } else {
      this.#rmp.mute = true;
    }
  }

  updateVolumeIcon(currentVolume) {
    const volumeElement = this.#rmp.dom.volume;
    if (currentVolume <= 0) {
      volumeElement.classList.add('rmp-i-volume-off');
    } else if (currentVolume > 0.5) {
      volumeElement.classList.add('rmp-i-volume');
    } else {
      volumeElement.classList.add('rmp-i-volume-half');
    }
  }

  destroy() {
    document.removeEventListener('mouseup', this.#volumeMouseupFn);
    document.removeEventListener('mousemove', this.#volumeMousemoveFn);
  }

  init() {
    const volumeLabel = this.#rmp.labels.hint.volume;
    // volume icon
    this.#rmp.dom.volume = FW.createElement(
      'button',
      'rmp-button rmp-volume rmp-i rmp-i-volume',
      null,
      null
    );
    this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.volume);
    let initialVolumeIconLabel = this.#rmp.labels.hint.mute;
    if (this.#rmp.mute) {
      initialVolumeIconLabel = this.#rmp.labels.hint.unmute;
    }
    this.#rmp.rmpAccessible.button(this.#rmp.dom.volume, initialVolumeIconLabel, true, null, 'm');
    const _onClickVolumeIconFn = this.#onClickVolumeIcon.bind(this);
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.volume, _onClickVolumeIconFn);
    // volumeBar
    this.#rmp.dom.volumeBar = FW.createElement('div', 'rmp-volume-bar');
    // on mobile force hide volumeBar
    if (this.#rmp.isMobile) {
      this.#rmp.dom.volumeBar.classList.add('rmp-no-display');
    }
    this.#rmp.rmpAccessible.button(this.#rmp.dom.volumeBar, volumeLabel);
    this.#rmp.dom.volumeBarSlidingArea = FW.createElement('div', 'rmp-volume-bar-sliding-area');
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.volumeBar, FW.stopPropagation);
    this.#rmp.dom.volumeBar.appendChild(this.#rmp.dom.volumeBarSlidingArea);
    this.#rmp.dom.volumeBarSlidingCurrentPosition = FW.createElement('div', 'rmp-volume-bar-sliding-current-position rmp-color-bg-sliding');
    const rmpVolumeBarSlidingHandle = FW.createElement('div', 'rmp-volume-bar-sliding-handle rmp-color-bg-sliding');
    this.#rmp.dom.volumeBarSlidingCurrentPosition.appendChild(rmpVolumeBarSlidingHandle);
    this.#rmp.dom.volumeBarSlidingArea.appendChild(this.#rmp.dom.volumeBarSlidingCurrentPosition);
    this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.volumeBar);
    // append time here to avoid has() support - no re-order support with CSS where no has() CSS support
    if (this.#rmp.dom.time) {
      this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.time);
    }
    // volume hint
    const volumeHint = FW.createElement(
      'div',
      'rmp-control-bar-hint rmp-color-bg',
      volumeLabel + ' (m)',
      this.#rmp.rtl
    );
    this.#rmp.dom.volume.appendChild(volumeHint);
    // initial value
    let volume = this.#rmp.initialVolume;
    if (typeof this.#rmp.dom.video.volume === 'number' && this.#rmp.dom.video.volume >= 0) {
      volume = this.#rmp.dom.video.volume;
    }
    this.#updateVolumeBarUI(volume);

    // wire UI
    this.#volumeMouseupFn = this.#volumeMouseup.bind(this);
    this.#volumeMousemoveFn = this.#volumeMousemove.bind(this);
    this.#rmp.dom.volumeBar.addEventListener('mousedown', event => {
      if (event) {
        event.stopPropagation();
      }
      this.#rmp.dom.volumeBarSlidingCurrentPosition.classList.add('grabbing');
      document.addEventListener('mouseup', this.#volumeMouseupFn);
      document.addEventListener('mousemove', this.#volumeMousemoveFn);
    });

    this.#rmp.onVolumeChangeFn = this.#onVolumeChange.bind(this);
    this.#rmp.dom.video.addEventListener('volumechange', this.#rmp.onVolumeChangeFn);
    /// update aria-valuenow when linear ad is on stage as well
    if (this.#rmp.ads) {
      this.#rmp.on('advolumechanged', () => {
        const currentVolume = this.#rmp.volume;
        this.#rmp.dom.volumeBar.setAttribute('aria-valuenow', FW.precisionRound(currentVolume * 100, 2));
        this.#rmp.dom.volumeBar.setAttribute('aria-valuetext',
          (FW.precisionRound(currentVolume * 100, 2)).toString() + '%');
      });
    }

    if (this.#rmp.hideVolume) {
      this.#rmp.dom.volume.classList.add('rmp-no-display');
    }
  }

}
